<template>
  <div class="container">
    <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
      <div class="popup-content">
        <div v-for="(note, index) in documentSelectedNotes" :key="index">
          <div>{{note.createdByUser?.fullName ?? note.createdBy}} - {{ note.createdAt ? new Date(note.createdAt).toLocaleString() : '—'}}</div>
          <div>{{note.note}}</div>
          <div class="dotted"></div>
        </div>

        <div>
          <Textarea v-model="newNote" :maxlength="NOTE_MAX_LENGTH" @input="updateRemainingChars" aria-label="Note Text" placeholder="Type a new note here ..."/>
        </div>

        <p>{{ remainingChars }} characters remaining</p>

        <Button class="btn btn-success" icon="pi pi-plus" label="Add Note" @click="addNote" rounded/>
      </div>
    </div>
  </div>
</template>

<script setup>
import {onMounted, ref} from 'vue';
import {useToast} from "primevue/usetoast";
import {useAppStore} from "@/stores/appStore.js";
import {NOTE_MAX_LENGTH} from "@/utils/constants.js";
import {createNote, getNotesByParent} from "@/services/noteService.js";
import {NoteModel} from "@/models/NoteModel.js";
import Button from "primevue/button";
import Textarea from "primevue/textarea";

const toast = useToast();
const appStore = useAppStore();

const newNote = ref('');
const documentSelectedNotes = ref([]);
const remainingChars = ref(NOTE_MAX_LENGTH);

const props = defineProps({
  parentType: {
    type: String,
    required: true,
  },
  parentId: {
    type: Number,
    required: true,
  },
  parentVersion: {
    type: Number,
    required: false,
  }
});
const emit = defineEmits(["close", "updated"]);

onMounted(async () => {
  await loadNotes();
});

async function loadNotes() {
  documentSelectedNotes.value = await getNotesByParent(
      props.parentType,
      props.parentId,
      props.parentVersion
  );
}

function updateRemainingChars() {
  remainingChars.value = NOTE_MAX_LENGTH - newNote.value.length;
}

async function addNote() {
  if (!newNote.value.trim()) {
    toast.add({ severity: "warn", summary: "Empty Note", detail: "Please enter a note." });
    return;
  }

  const note = new NoteModel();
  note.parentType = props.parentType;
  note.parentId = props.parentId;
  note.parentVersion = props.parentVersion;
  note.createdBy = appStore.user.id;
  note.note = newNote.value;

  await createNote(note);
  await loadNotes();

  newNote.value = "";
  remainingChars.value = NOTE_MAX_LENGTH;

  emit("updated"); // tell parent something changed
}
</script>

<style scoped>
.popup-content {
  max-height: 70vh;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}


.popup-content div {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

div.dotted {
  border-top: 1px dotted black;
  margin: 10px 0;
}

textarea {
  width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
